import type { SettleDeclined, SettlePackageOption } from '@/lib/api'

// ─── The calendar → chat payment handoff ────────────────────────────────────────
//
// When the calendar's Pay action cannot spend a credit on its own, it hands the
// whole picture to operator chat as a MESSAGE. The generator, the detector, and
// the collapsed summary live together in this module so their formats cannot
// drift apart: isSettleHandoff() recognises exactly what buildSettleHandoff()
// produces, and MessageBubble uses that to render this one message type
// collapsed (the ids stay in the text for the model; the operator sees a human
// summary until they ask for details).

function packageLine(p: SettlePackageOption): string {
  const why =
    p.ineligible_reason === 'expired' ? 'EXPIRED'
      : p.ineligible_reason === 'exhausted' ? 'no sessions left'
      : p.ineligible_reason === 'service_exhausted' ? 'no sessions left for this service (others may remain)'
      : p.ineligible_reason === 'service_not_covered' ? 'does not cover this service'
      // Two facts the model must not conflate. insufficient_credits is a
      // package that WORKS and is simply too small for this treatment — it can
      // still pay for a cheaper one, so "no sessions left" would send the
      // operator to sell a replacement they do not need.
      : p.ineligible_reason === 'insufficient_credits'
        ? `only ${p.sessions_remaining} credit${p.sessions_remaining === 1 ? '' : 's'} left, this treatment costs ${p.credit_cost ?? 1}`
      : p.ineligible_reason === 'category_mismatch' ? 'this treatment is no longer in the package’s category'
      : p.ineligible_reason === 'voided' ? 'sale was reversed'
      : 'usable'
  const expiry = p.expires_at ? `expires ${p.expires_at.slice(0, 10)}` : 'no expiry'
  // Booking-scoped counts: on a per-service package they are THIS service's own
  // allowance, and the line says so — the model reads this text verbatim.
  const scope = p.per_service ? ' for this service' : ''
  // The cost rides the line whenever it is not the ordinary one credit. The
  // model reads this verbatim and cannot see the booking, so without it a
  // "5 of 10 left" package refusing a booking looks arbitrary.
  const cost = (p.credit_cost ?? 1) > 1 ? `, this treatment costs ${p.credit_cost} credits` : ''
  return `- ${p.name} (id ${p.customer_package_id}): ${p.sessions_remaining} of ${p.sessions_total}${scope} left${cost}, ${expiry} — ${why}`
}

export const SETTLE_HANDOFF_PREFIX = 'Take payment for booking '

/**
 * The handoff message for a booking no credit could cover.
 *
 * Every id the AI might need is spelled out in the TEXT. Chat history is
 * text-only — tool results are discarded after one turn — so an id that exists
 * only in this payload and not in the transcript is an id the model will later
 * invent (see CLAUDE.md → the widget's fabricated service_id outage).
 */
export function buildSettleHandoff(d: SettleDeclined): string {
  const who = d.customer_name ?? 'the customer'
  const why = d.reason === 'multiple_eligible_packages'
    ? 'Several of their packages could cover it, so I did not pick one — tell me which credit to spend, or take a payment instead.'
    : `I tried to pay this off a package credit and it did not apply (${d.reason}).`
  const lines = [
    `${SETTLE_HANDOFF_PREFIX}${d.booking_id} — ${d.service_name ?? 'session'} for ${who} (customer ${d.customer_id}) on ${d.start.slice(0, 16).replace('T', ' ')}.`,
    `Outstanding: ${d.currency} ${d.balance_due.toFixed(2)} of ${d.currency} ${d.price.toFixed(2)}.`,
    '',
    why,
  ]
  if (d.packages.length) {
    lines.push(`${who}'s packages:`, ...d.packages.map(packageLine))
  } else {
    lines.push(`${who} holds no packages at all.`)
  }
  lines.push('', 'What should we do?')
  return lines.join('\n')
}

/** True only for messages produced by buildSettleHandoff (the Pay handoff). */
export function isSettleHandoff(text: string): boolean {
  return text.startsWith(SETTLE_HANDOFF_PREFIX)
}

/**
 * The collapsed view of a handoff: its first two lines (what + how much) with
 * the machine ids stripped. The full text — ids and all — stays in the entry
 * and in what is sent to the model; this is display only.
 */
export function settleHandoffSummary(text: string): string {
  const [first = '', second = ''] = text.split('\n')
  const cleaned = first
    .replace(/^Take payment for booking \S+ — /, 'Take payment for ')
    .replace(/ \(customer [^)]*\)/, '')
  return second ? `${cleaned}\n${second}` : cleaned
}
